/**
 * The pop-up that reaches somebody who is not looking at the product.
 *
 * A toast is only a notification while the tab is in front of you. A reminder about a meeting in ten
 * minutes has to arrive while you are in a document, a spreadsheet or another browser entirely —
 * which is the one thing a toast cannot do and the whole reason this exists.
 *
 * Three rules it follows, and they are all about not being the reason somebody turns notifications
 * off:
 *
 * **It never asks on page load.** A permission prompt somebody did not invite is the thing everyone
 * has learned to click "block" on, and a block is permanent. `requestDesktopPermission` is called
 * from a switch in the preferences screen, by somebody who has just said they want this.
 *
 * **It is silent while the tab is visible.** If you are looking at the product you already have the
 * toast and the bell; a desktop notification as well is the same thing said three times.
 *
 * **It says one thing once.** Notifications are tagged by their id, so a re-render, a refetch or a
 * second tab replaces the pop-up rather than stacking another copy of it.
 */

/** Whether this browser can do it at all. Absent in an insecure context and in some embedded views. */
export function desktopNotificationsSupported(): boolean {
  return typeof globalThis !== 'undefined' && 'Notification' in globalThis;
}

export type DesktopPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function desktopPermission(): DesktopPermission {
  if (!desktopNotificationsSupported()) return 'unsupported';

  return Notification.permission as DesktopPermission;
}

/**
 * Asks, once, from a gesture.
 *
 * Returns what the browser decided rather than a boolean, because "denied" and "dismissed" are
 * different things to a screen: one is a settled answer and the other means the question can be
 * asked again later.
 */
export async function requestDesktopPermission(): Promise<DesktopPermission> {
  if (!desktopNotificationsSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission as DesktopPermission;

  try {
    return (await Notification.requestPermission()) as DesktopPermission;
  } catch {
    // Older browsers took a callback and reject the promise form. Nothing is broken; nothing is
    // shown either, which is the safe direction.
    return 'default';
  }
}

export interface DesktopNotice {
  id: string;
  title: string;
  body?: string;
  /** Where clicking it goes, inside the product. Never an external URL. */
  link?: string | null;
}

/**
 * Shows one, if the person has said yes and is not already looking at the page.
 *
 * `onOpen` rather than a navigation performed here: this module knows nothing about the router, and
 * a click has to focus the window before it navigates or the person lands on the right page in a
 * tab they cannot see.
 */
export function showDesktopNotice(
  notice: DesktopNotice,
  onOpen: (link: string) => void,
): boolean {
  if (desktopPermission() !== 'granted') return false;

  // Looking at the page already: the toast has it covered.
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return false;

  try {
    const shown = new Notification(notice.title, {
      ...(notice.body ? { body: notice.body } : {}),
      // One pop-up per subject: a refetch or a second tab replaces it rather than stacking.
      tag: notice.id,
      // The browser decides whether that replacement makes a sound; a silent update is right.
      renotify: false,
    } as NotificationOptions);

    shown.onclick = () => {
      globalThis.focus();
      shown.close();

      if (notice.link) onOpen(notice.link);
    };

    return true;
  } catch {
    // A browser that refuses to construct one — a permissions policy, a private window — is not a
    // reason for anything else to fail.
    return false;
  }
}
